  import React from 'react';
import { CommandBar, ICommandBarItemProps, SelectionMode, DetailsListLayoutMode, IColumn, ShimmeredDetailsList, ConstrainMode, SearchBox, IDetailsListProps, IDetailsRowStyles, DetailsRow, IDetailsListStyles, ScrollablePane, IRenderFunction, IDetailsHeaderProps, IDetailsColumnRenderTooltipProps, TooltipHost, Sticky, StickyPositionType, ScrollbarVisibility, mergeStyleSets, Stack } from '@fluentui/react';
import { IGuildStats, IStakeholder } from '../../models';
import { ApplicationContext } from '../../contexts/ApplicationContext';
import { useAuthenticationContext } from '../../hooks/useAuthenticationContext';
import appstyles from './../../styles/App.module.scss';
import { IGuild } from '../../models/IGuild';
import GuildPicker from '../../components/GuildPicker';
import StakeholderPanel from './StakeholderPanel';
import FreezableDetailsList, { IFilterableColumn } from '../../components/FreezableDetailsList';
import useQueryState from '../../hooks/useQueryState';
import ListComponent from '../../components/ListComponent';

interface IStakeholdersProps
{

}

type StakeholderView = "stakeholders" | "all";

export const Stakeholders: React.FunctionComponent<IStakeholdersProps> = (props: React.PropsWithChildren<IStakeholdersProps>) => {
  //let _allStakeholders: IStakeholder[] = [];

  const { Services } = React.useContext(ApplicationContext);
  const [stakeholders, setStakeholders] = React.useState<IStakeholder[]>([]);  
  const [allStakeholders, setAllStakeholders] = React.useState<IStakeholder[]>([]);  
  const [selected, setSelected] = React.useState<IStakeholder | undefined>(undefined);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [showStakeholderPanel, setShowStakeholderPanel] = React.useState<boolean>(false);
  const [currentView, setCurrentView] = React.useState<StakeholderView>("stakeholders");
  
  const [personId, setPersonId] = useQueryState<number>("personId", undefined);

  const { user, guild, setCurrentGuild, getCurrentGuildStats, setCurrentGuildStats } = useAuthenticationContext();
  const [showGuildPicker, setShowGuildPicker] = React.useState<boolean>(false);
  React.useEffect(() => {
    if(user != null && user.guilds.length > 1 && guild == null)
    {
      setStakeholders([]);
      setShowGuildPicker(true);
    } 
  }, []);

  React.useEffect(() => {
    loadStakeholders("stakeholders");
  }, []);

  const loadStakeholders = async (view: StakeholderView = currentView) => {
    setIsLoading(true);
    const request = view === "all"
      ? Services?.Stakeholder.GetStakeholders(true)
      : Services?.Stakeholder.GetStakeholders(false);

    request?.then((stakeholders: IStakeholder[]) => {
      setAllStakeholders(stakeholders);
      setStakeholders(stakeholders);        
      setIsLoading(false);

      setCurrentGuildStats({ stakeholderCount: stakeholders.length, shareCount: stakeholders.map(x => x.shareCount).reduce((prev, current ) => prev+current, 0) } as IGuildStats);

      if(personId)
      {
        const stakeholder = stakeholders.find(x => x.personID == personId);
        if(stakeholder)
        {
          setSelected(stakeholder);
          setShowStakeholderPanel(true);
        }
      }        
    });
  }

  const getCurrentViewText = (): string => currentView === "stakeholders" ? "Alle interessenter" : "Alle personer";
  
  const COLUMNS = [
    {
      key: 'personID',
      name: 'Person ID',
      fieldName: 'personID',
      minWidth: 100,
      maxWidth: 100,
      isResizable: false,
      filter: true
    },
    {
      key: 'firstName',
      name: 'Fornavn',
      fieldName: 'firstName',
      minWidth: 100,
      maxWidth: 150,
      isResizable: true,
      filter: true
    },
    {
      key: 'lastName',
      name: 'Efternavn',
      fieldName: 'lastName',
      minWidth: 100,
      maxWidth: 150,
      isResizable: true,
      filter: true
    },
    {
      key: 'address',
      name: 'Gadevej',
      fieldName: 'address',
      minWidth: 100,
      maxWidth: 150,
      isResizable: true,
      filter: true
    },
    {
      key: 'houseNo',
      name: 'Hus nr.',
      fieldName: 'houseNo',
      minWidth: 100,
      maxWidth: 100,
      isResizable: true,
      filter: true
    },
    {
      key: 'zipCode',
      name: 'Post nr.',
      fieldName: 'zipCode',
      minWidth: 100,
      maxWidth: 100,
      isResizable: false,
      filter: true
    },
    {
      key: 'city',
      name: 'By',
      fieldName: 'city',
      minWidth: 100,
      maxWidth: 150,
      isResizable: true,
      filter: true
    }
  ] as IFilterableColumn[];

  const CreateFarMenuItems = [
      {
        key: "viewSelector",
        text: getCurrentViewText(),
        iconProps: { iconName: 'List' },
        subMenuProps: {
          items: [
            {
              key: 'stakeholders',
              text: 'Alle interessenter',              
              checked: currentView === 'stakeholders',
              onClick: () => {
                setCurrentView('stakeholders');
                loadStakeholders('stakeholders');
              }
            },
            {
              key: 'all',
              text: 'Alle',              
              checked: currentView === 'all',
              onClick: () => {
                setCurrentView('all');
                loadStakeholders('all');
              }
            }
          ]
        }
      },
      {        
        key:"search",
        onRender:() => <SearchBox 
          placeholder="Søg efter ..." 
          className={appstyles.searchBox} 
          onSearch={(newValue) => { onSearch(newValue); } } 
          onChange={(event?: React.ChangeEvent<HTMLInputElement>, newValue?: string) => { onSearch(newValue); }}
          onClear={ev => { onSearch(""); }}
          />
      }
    ];

    const onSearch = (searchTerm: string | undefined) => {
      setStakeholders(searchTerm && searchTerm != "" ? filter(searchTerm) : allStakeholders);
    }

    const filter = (searchTerm: string) : IStakeholder[] => {
      const regex = new RegExp(searchTerm, 'i');

      return allStakeholders.filter(i => 
        (i.personID + "").toLowerCase().indexOf(searchTerm.toLocaleLowerCase()) > -1 ||
        (i.firstName + "").toLowerCase().indexOf(searchTerm.toLocaleLowerCase()) > -1 || 
        (i.lastName + "").toLowerCase().indexOf(searchTerm.toLocaleLowerCase()) > -1 ||
        (i.address + "").toLowerCase().indexOf(searchTerm.toLocaleLowerCase()) > -1 ||
        (i.houseNo + "").toLowerCase().indexOf(searchTerm.toLocaleLowerCase()) > -1 ||
        (i.zipCode + "").toLowerCase().indexOf(searchTerm.toLocaleLowerCase()) > -1 ||
        (i.city + "").toLowerCase().indexOf(searchTerm.toLocaleLowerCase()) > -1 || 
        (i.cpr + "").toLowerCase().indexOf(searchTerm.toLocaleLowerCase()) > -1 || 
        (i.cvr + "").toLowerCase().indexOf(searchTerm.toLocaleLowerCase()) > -1 ||
        (i.accounts?.some(account => regex.test(account)) || false)
      );
    }

    const COMMANDS: ICommandBarItemProps[] = [    
      {
        key: 'create',
        text: 'Ny interessent',
        iconProps: { iconName: 'Add' },
        onClick: () => { 
            setSelected(undefined);
            setShowStakeholderPanel(true);
         }
      }    
    ];
    if(showGuildPicker) return <GuildPicker onSelect={(guild: IGuild | undefined) => { setCurrentGuild(guild as IGuild); setShowGuildPicker(false); loadStakeholders(); } } />
  
    const stats = getCurrentGuildStats();
    const hasStats = typeof stats?.shareCount === "number" && typeof stats?.stakeholderCount === "number";
    const title = currentView === "stakeholders" && hasStats
      ? `Alle interessenter i ${guild?.name} (${new Intl.NumberFormat('da').format(stats!.shareCount)} andele fordelt på ${new Intl.NumberFormat('da').format(stats!.stakeholderCount)} interessenter)`
      : `Alle interessenter i ${guild?.name}`;

    return <div className={appstyles.contentContainer}>
      <ListComponent commands={COMMANDS} farItems={CreateFarMenuItems} title={title}>
        <FreezableDetailsList 
                items={stakeholders}
                columns={COLUMNS}   
                setKey="keyStakeholders"
                selectionMode={SelectionMode.single}
                enableClickRowOpen={true}
                onSelectionChanged={(item:any) => {  
                  if(item !== undefined)
                  {                  
                    setSelected(item); 
                    setShowStakeholderPanel(true); }}                
                  }
              />     
        </ListComponent>
        {showStakeholderPanel && <StakeholderPanel selectedStakeholder={selected} onChanged={(stakeholder) => {
          // Nye arrays, så den filtrerede liste i FreezableDetailsList også opdateres
          const replace = (list: IStakeholder[]) => list.map(x => x.personID == stakeholder.personID ? stakeholder : x);
          setStakeholders(replace);
          setAllStakeholders(replace);
          setShowStakeholderPanel(false); }} onDismiss={() => { setShowStakeholderPanel(false);  } } />}            
      </div>
  }

export default Stakeholders;